import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";
import React, { useEffect } from "react";

import CrudTableControl from "@components/CrudTableControl";
import CrudTableHead from "@components/CrudTableHead";
import CrudTableToolbar from "@components/CrudTableToolbar";
import TableMenu from "@components/TableMenu";
import { uniqueKey } from "@utils/react";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {},
  tableCell: {
    paddingTop: "15px",
    paddingBottom: "15px",
    textAlign: "center",
  },
  menuCell: {
    margin: "-16px",
  },
  basicTitle: {
    paddingLeft: "16px",
    paddingRight: "8px",
    lineHeight: "64px",
  },
});

function CrudTable(props) {
  const classes = useStyles();
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [page, setPage] = React.useState(props.page);
  const [rowsPerPage, setRowsPerPage] = React.useState(props.rowsPerPage);
  const [order, setOrder] = React.useState(props.order);
  const [orderBy, setOrderBy] = React.useState(props.orderBy);

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function renderColumnValue(row, column) {
    const value = row[column.id];

    if (column.format && typeof column.type === "number") {
      return column.format(value);
    }

    if (column.render) {
      return column.render(value, column, row);
    }

    if (column.type === "menu") {
      return (
        <div className={classes.menuCell}>
          <TableMenu options={value} data={row} />
        </div>
      );
    }

    return value;
  }

  function renderColumn(row, column) {
    return (
      <TableCell key={uniqueKey("table-cell")} align={column.align}>
        {renderColumnValue(row, column)}
      </TableCell>
    );
  }

  function renderRow(row, index) {
    const isItemSelected = isSelected(row.id);
    const labelId = `crud-table-checkbox-${index}`;

    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={uniqueKey("table-row")}
        aria-checked={isItemSelected}
        selected={isItemSelected}
      >
        {props.rowsSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isItemSelected}
              inputProps={{ "aria-labelledby": labelId }}
              onChange={(event) => handleSelectRow(event, row.id)}
            />
          </TableCell>
        )}
        {props.columns.map((column) => renderColumn(row, column))}
      </TableRow>
    );
  }

  function renderLoadingMessage() {
    return (
      <TableRow>
        <TableCell
          className={classes.tableCell}
          colSpan={props.columns.length + 1}
        >
          {props.loadingMessage}
        </TableCell>
      </TableRow>
    );
  }

  function renderEmptyMessage() {
    return (
      <TableRow>
        <TableCell
          className={classes.tableCell}
          colSpan={props.columns.length + 1}
        >
          {props.noItemsMessage}
        </TableCell>
      </TableRow>
    );
  }

  function isSelected(id) {
    return props.selectedRows.indexOf(id) !== -1;
  }

  function getProcessedRows() {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }

  function handleRequestSort(event, property) {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      const newSelecteds = filteredRows.map((n) => n.id);
      props.onSelectRow(newSelecteds);
      return;
    }
    props.onSelectRow([]);
  }

  function handleSelectRow(event, id) {
    const selectedIndex = props.selectedRows.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(props.selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(props.selectedRows.slice(1));
    } else if (selectedIndex === props.selectedRows.length - 1) {
      newSelected = newSelected.concat(props.selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        props.selectedRows.slice(0, selectedIndex),
        props.selectedRows.slice(selectedIndex + 1)
      );
    }
    props.onSelectRow(newSelected);
  }

  function handleDeleteSelectedRows(selectedRows) {
    props.onDeleteRows(
      filteredRows.filter((row) => selectedRows.includes(row.id))
    );
  }

  function handleExportPDF() {}

  useEffect(() => {
    if (!props.filter) {
      setFilteredRows(props.rows);
    }
  }, [props.rows]);

  return (
    <React.Fragment>
      {/* toolbar */}
      {props.rowsSelection &&
        (props.title || props.selectedRows.length > 0) && (
          <CrudTableToolbar
            title={props.title}
            selectedRows={props.selectedRows}
            onDelete={handleDeleteSelectedRows}
          />
        )}
      {/* end toolbar */}
      {/* title */}
      {props.title && !props.rowsSelection && (
        <Typography className={classes.basicTitle} color="inherit" variant="h6">
          {props.title}
        </Typography>
      )}
      {/* end title */}
      {/* control */}
      {props.control && (
        <CrudTableControl
          rows={props.rows}
          filter={props.filter}
          filterFields={props.filterFields}
          refresh={props.refresh}
          onFilter={(rows) => {
            setFilteredRows(rows);
          }}
          onRefresh={props.onRefreshRows}
          onExportPDF={handleExportPDF}
        />
      )}
      {/* end control */}
      <TableContainer className={classes.container}>
        <Table aria-label="">
          {/* header */}
          <CrudTableHead
            headCells={props.columns}
            numSelected={props.selectedRows.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={filteredRows.length}
            rowsSelection={props.rowsSelection}
          />
          {/* end header */}
          {/* body */}
          <TableBody>
            {!props.loading && getProcessedRows().map(renderRow)}

            {/* loading */}
            {props.loading && renderLoadingMessage()}
            {/* end loading */}

            {/* empty */}
            {filteredRows.length === 0 &&
              !props.loading &&
              renderEmptyMessage()}
            {/* end empty */}
          </TableBody>
          {/* end body */}
        </Table>
      </TableContainer>
      {/* pagination */}
      <TablePagination
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to === -1 ? count : to} de ${
            count !== -1 ? count : `mais do que ${to}`
          }`
        }
        rowsPerPageOptions={props.rowsPerPageOptions}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {/* end pagination */}
    </React.Fragment>
  );
}

CrudTable.propTypes = {
  title: PropTypes.string,
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  rowsPerPageOptions: PropTypes.array,
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
  selectedRows: PropTypes.array,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  rowsSelection: PropTypes.bool,
  loading: PropTypes.bool,
  noItemsMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  control: PropTypes.bool,
  filter: PropTypes.bool,
  filterFields: PropTypes.array,
  refresh: PropTypes.bool,
  onSelectRow: PropTypes.func,
  onDeleteRows: PropTypes.func,
  onRefreshRows: PropTypes.func,
};

CrudTable.defaultProps = {
  title: "",
  rows: [],
  columns: [],
  rowsPerPageOptions: [10, 25, 100],
  rowsPerPage: 10,
  page: 0,
  selectedRows: [],
  order: "asc",
  orderBy: "",
  rowsSelection: false,
  loading: false,
  noItemsMessage: "Nenhum registro encontrado",
  loadingMessage: "Carregando...",
  control: true,
  filter: false,
  filterFields: [],
  refresh: true,
  onSelectRow: () => {},
  onDeleteRows: () => {},
  onRefreshRows: () => {},
};

export default CrudTable;
